import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Star, ArrowDown, ShoppingBag, Eye, Flame } from "lucide-react";
import { MaskLine } from "./motion";
import { IMAGES } from "@/lib/product";

const RECENT_ORDERS = [
  { name: "Marcus in Austin", item: "6 Bags · God Mode", ago: "2 min ago" },
  { name: "Priya in Denver", item: "3 Bags · God Mode", ago: "just now" },
  { name: "Tyler in Seattle", item: "3 Bags · God Mode", ago: "4 min ago" },
  { name: "Jordan in Miami", item: "6 Bags · God Mode", ago: "1 min ago" },
  { name: "Alicia in NYC", item: "1 Bag · God Mode", ago: "just now" },
];

const CountdownTimer = () => {
  const [left, setLeft] = useState(600);
  useEffect(() => {
    const KEY = "oc_deal_deadline";
    let end = Number(localStorage.getItem(KEY));
    if (!end || end < Date.now()) {
      end = Date.now() + 10 * 60 * 1000;
      localStorage.setItem(KEY, String(end));
    }
    setLeft(Math.max(0, Math.floor((end - Date.now()) / 1000)));
    const t = setInterval(() => {
      setLeft((p) => {
        if (p <= 1) {
          const ne = Date.now() + 10 * 60 * 1000;
          localStorage.setItem(KEY, String(ne));
          return 600;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);
  const m = String(Math.floor(left / 60)).padStart(2, "0");
  const s = String(left % 60).padStart(2, "0");
  return (
    <div className="mt-8 flex items-center gap-3 border border-blaze/40 bg-blaze/5 px-4 py-3">
      <Flame className="h-4 w-4 flex-none text-blaze" />
      <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-300">
        Launch offer ends in
      </span>
      <span className="font-display text-xl leading-none text-blaze tabular-nums">
        {m}:{s}
      </span>
      <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500 sm:block">
        Free shipping · no minimum
      </span>
    </div>
  );
};

const LiveCount = () => {
  const [n, setN] = useState(48);
  useEffect(() => {
    const t = setInterval(() => {
      setN((p) => Math.min(93, Math.max(31, p + (Math.floor(Math.random() * 7) - 3))));
    }, 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-200">
      {n} viewing now
    </span>
  );
};

const LiveProof = () => {
  const [i, setI] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => {
        setI((p) => (p + 1) % RECENT_ORDERS.length);
        setShow(true);
      }, 400);
    }, 3800);
    return () => clearInterval(t);
  }, []);
  const o = RECENT_ORDERS[i];
  return (
    <div className="pointer-events-none absolute -bottom-4 -left-4 z-30 sm:-left-8">
      <AnimatePresence mode="wait">
        {show && (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-3 border border-zinc-700 bg-zinc-950/90 px-4 py-3 shadow-2xl backdrop-blur-xl"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center bg-blaze">
              <ShoppingBag className="h-4 w-4 text-black" />
            </span>
            <div>
              <div className="text-xs font-semibold text-white">{o.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-400">
                {o.item} · {o.ago}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const scrollToBuy = () => {
  document.getElementById("buy")?.scrollIntoView({ behavior: "smooth" });
};
const scrollToStory = () => {
  document.getElementById("manifesto")?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bagY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-24"
    >
      {/* Background pour */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.pour}
          alt=""
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/60 to-zinc-950" />
        <div className="absolute inset-0 bg-[#09090B]/40" />
      </motion.div>

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-8 px-5 pb-16 sm:px-8 lg:grid-cols-12">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-blaze"
          >
            <span className="h-px w-8 bg-blaze" />
            Overclock · Est. for high performers
          </motion.p>

          <h1 className="font-display uppercase leading-[0.82] tracking-tight text-white">
            <span className="block text-6xl sm:text-7xl lg:text-8xl text-zinc-400">
              <MaskLine delay={0.25}>Enter</MaskLine>
            </span>
            <span className="block text-7xl sm:text-8xl lg:text-[10rem]">
              <MaskLine delay={0.38}>
                <span className="text-white">GOD </span>
                <span className="text-blaze">MODE.</span>
              </MaskLine>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg"
          >
            The functional mushroom coffee engineered for people who refuse to
            slow down. All-day focus. Real energy.{" "}
            <span className="text-white font-semibold">
              Zero jitters. Zero crash. Zero compromise.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
          >
            <button
              data-testid="hero-cta-btn"
              onClick={scrollToBuy}
              className="group flex items-center gap-3 bg-blaze px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.12em] text-black shadow-[6px_6px_0_0_rgba(255,63,0,0.25)] transition-transform duration-200 hover:-translate-y-1 hover:translate-x-1 hover:shadow-[3px_3px_0_0_rgba(255,63,0,0.35)]"
            >
              Claim God Mode — $41.95
            </button>
            <button
              data-testid="hero-story-btn"
              onClick={scrollToStory}
              className="flex items-center gap-2 border border-zinc-700 px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 transition-colors duration-200 hover:border-white hover:text-white"
            >
              Why it works <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </motion.div>

          <CountdownTimer />

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-5 border-t border-zinc-800 pt-6"
          >
            <Stat
              top={
                <span className="flex items-center gap-1 text-blaze">
                  4.9 <Star className="h-4 w-4 fill-blaze text-blaze" />
                </span>
              }
              label="2,900+ reviews"
            />
            <Stat top="12,000+" label="orders shipped" />
            <Stat top="0" label="afternoon crashes" />
          </motion.div>
        </div>

        {/* Right bag — conversion showcase */}
        <div className="relative lg:col-span-5">
          <motion.div style={{ y: bagY, opacity: fade, perspective: 1200 }} className="relative mx-auto max-w-md">
            {/* live viewers badge */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1 }}
              className="absolute -right-2 top-2 z-30 flex items-center gap-2 border border-zinc-700 bg-zinc-950/90 px-3 py-1.5 backdrop-blur-xl sm:-right-6"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
              <Eye className="h-3.5 w-3.5 text-zinc-300" />
              <LiveCount />
            </motion.div>

            {/* rotating spotlight + glow */}
            <motion.div
              aria-hidden
              animate={{ rotate: 360 }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              className="absolute left-1/2 top-1/2 -z-10 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ background: "conic-gradient(from 0deg, transparent 0deg, rgba(255,63,0,0.25) 60deg, transparent 140deg, transparent 360deg)", filter: "blur(30px)" }}
            />
            <motion.div
              aria-hidden
              animate={{ opacity: [0.35, 0.65, 0.35], scale: [1, 1.08, 1] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute left-1/2 top-1/2 -z-10 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blaze/25 blur-[100px]"
            />

            {/* floating particles */}
            {[0, 1, 2, 3, 4].map((p) => (
              <motion.span
                key={p}
                aria-hidden
                className="absolute left-1/2 top-1/2 h-1 w-1 rounded-full bg-blaze/70"
                style={{ marginLeft: (p - 2) * 46 }}
                animate={{ y: [40, -120], opacity: [0, 1, 0] }}
                transition={{ duration: 4 + p, repeat: Infinity, ease: "easeOut", delay: p * 0.7 }}
              />
            ))}

            {/* product with smooth 3D showcase spin — tap to buy */}
            <motion.div
              onClick={scrollToBuy}
              role="button"
              aria-label="Shop God Mode"
              title="Tap to claim yours"
              className="cursor-pointer"
              animate={{ y: [0, -18, 0], rotateY: [-14, 14, -14], rotateZ: [-1.5, 1.5, -1.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <motion.img
                src={IMAGES.bagHero}
                alt="OVERCLOCK God Mode Mushroom Coffee bag"
                data-testid="hero-product-image"
                initial={{ opacity: 0, scale: 0.9, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="mx-auto w-full rounded-xl drop-shadow-2xl"
              />
            </motion.div>

            {/* price anchor chip */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3 }}
              className="absolute -right-3 bottom-16 z-30 border border-blaze/50 bg-zinc-950/90 px-4 py-2 text-right backdrop-blur-xl sm:-right-6"
            >
              <div className="flex items-baseline justify-end gap-2">
                <span className="font-mono text-[11px] text-zinc-500 line-through">$52</span>
                <span className="font-display text-2xl leading-none text-white">$41.95</span>
              </div>
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-emerald-400">
                Free shipping · no minimum
              </div>
            </motion.div>

            {/* real-time purchase pop */}
            <LiveProof />
          </motion.div>

          {/* scarcity + rating strip under product */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
            className="mx-auto mt-8 flex max-w-md flex-col items-center gap-3 sm:flex-row sm:justify-between"
          >
            <div className="flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-blaze text-blaze" />
                ))}
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                Loved by 12,000+
              </span>
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-blaze">
              <Flame className="h-3.5 w-3.5" />
              Selling fast · low stock
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ top, label }) => (
  <div>
    <div className="font-display text-2xl leading-none text-white sm:text-3xl">
      {top}
    </div>
    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
      {label}
    </div>
  </div>
);
